import {
  Tabs,
  Button,
  Form,
  Input,
  Avatar,
  Switch,
  Row,
  Col,
  List,
  Spin,
  Modal,
  Dropdown,
  Select,
  ConfigProvider,
} from "antd";
import { useAntdAppMessage } from "../../hooks/useAntdAppMessage";
import CustomButton from "../../components/Button";
import MainLayout from "../../layouts/Layout";
import {
  UserOutlined,
  DeleteOutlined,
  SettingOutlined,
  EditOutlined,
  FilterOutlined,
  MenuOutlined,
  DownOutlined,
  TeamOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useAtomValue } from "jotai";
import { userAtom } from "../../store/atoms";
import { useSecureService } from "../../store/service";
import { Fragment, useState, useEffect, useCallback, useRef, useMemo, type ChangeEvent } from "react";
import styles from "./settings.module.scss";
import layoutStyles from "../../layouts/mainLayout.module.scss";
import projectsStyles from "../projects/Projects.module.scss";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import deleteModalStyles from "../../components/DeleteModal/DeleteModal.module.scss";
import { AddUserForm } from "../user/AddUserForm";
import type { RevisdomAPIViewModelsUserResponse } from "../../service/models";
import { RevisdomAPIViewModelsUserResponseRoleEnum } from "../../service/models/RevisdomAPIViewModelsUserResponse";
import { canManageUserAccounts, LEGACY_ROLE_TO_WIRE, Role } from "../../constants/roles";
import { useAuth } from "../../hooks/useAuth";

const SUPER_ADMIN_ROLES = new Set<string>([
  Role.SuperAdmin,
  RevisdomAPIViewModelsUserResponseRoleEnum.SperYnetici4,
]);

function isSuperAdminRole(role: string | undefined): boolean {
  if (!role) return false;
  if (SUPER_ADMIN_ROLES.has(role)) return true;
  if (role === "4") return true;
  const p = role.match(/\((\d+)\)\s*$/);
  if (p && parseInt(p[1], 10) === 4) return true;
  return false;
}

function canDeleteListUser(
  u: RevisdomAPIViewModelsUserResponse,
  currentUserId?: number,
  currentUserEmail?: string | null,
  viewerRole?: string
): boolean {
  if (u.isDeleted) return false;
  if (u.id == null) return false;
  if (currentUserId != null && u.id === currentUserId) return false;
  if (
    currentUserEmail &&
    u.email &&
    u.email.toLowerCase() === currentUserEmail.toLowerCase()
  ) {
    return false;
  }
  if (u.role != null && isSuperAdminRole(String(u.role))) {
    return false;
  }
  // Admin (SuperAdmin değil): başka Admin veya SuperAdmin satırını silemez; SuperAdmin silebilir
  if (viewerRole != null) {
    const v = String(viewerRole);
    if (v === Role.Admin && !isSuperAdminRole(v) && u.role != null) {
      const target = String(u.role);
      if (target === Role.Admin || isSuperAdminRole(target)) {
        return false;
      }
    }
  }
  return true;
}

/** Sayısal / OpenAPI metin rol → wire etiket (filtre + liste; "Admin (0)" gösterilmez) */
const USER_ROLE_NUM_TO_LABEL: Record<number, string> = {
  0: Role.Admin,
  1: Role.Editor,
  2: Role.Commenter,
  3: Role.Viewer,
  4: Role.SuperAdmin,
  5: Role.Guest,
};

const SHORT_USER_ROLE_TO_LABEL: Record<string, string> = {
  [Role.Admin]: USER_ROLE_NUM_TO_LABEL[0],
  [Role.Editor]: USER_ROLE_NUM_TO_LABEL[1],
  [Role.Commenter]: USER_ROLE_NUM_TO_LABEL[2],
  [Role.Viewer]: USER_ROLE_NUM_TO_LABEL[3],
  [Role.SuperAdmin]: Role.SuperAdmin,
  [Role.Guest]: USER_ROLE_NUM_TO_LABEL[5],
  AccountManager: USER_ROLE_NUM_TO_LABEL[0],
  AccountOwner: USER_ROLE_NUM_TO_LABEL[0],
  InternalTeam: USER_ROLE_NUM_TO_LABEL[1],
  BrandAccountViewer: USER_ROLE_NUM_TO_LABEL[3],
};

function resolveUserRoleLabel(role: unknown): string {
  if (role === null || role === undefined) return "";
  if (typeof role === "number" && Number.isFinite(role)) {
    const n = role as number;
    return USER_ROLE_NUM_TO_LABEL[n] ?? "";
  }
  let s = String(role).trim();
  if (!s) return "";
  /* OpenAPI / UI: "Admin (0)", "Süper Yönetici (4)" → wire etiket */
  const paren = s.match(/\((\d+)\)\s*$/);
  if (paren) {
    const n = parseInt(paren[1], 10);
    if (Number.isFinite(n) && USER_ROLE_NUM_TO_LABEL[n]) {
      return USER_ROLE_NUM_TO_LABEL[n];
    }
  }
  if (LEGACY_ROLE_TO_WIRE[s]) s = LEGACY_ROLE_TO_WIRE[s];
  if (SHORT_USER_ROLE_TO_LABEL[s]) return SHORT_USER_ROLE_TO_LABEL[s];
  if (/^\d+$/.test(s)) {
    const n = parseInt(s, 10);
    return USER_ROLE_NUM_TO_LABEL[n] ?? s;
  }
  return s;
}

function displayNameForUser(u: RevisdomAPIViewModelsUserResponse): string {
  return (
    [u.firstName, u.lastName].filter(Boolean).join(" ").trim() ||
    u.email ||
    ""
  );
}

/** Liste her zaman kullanıcı adına (ad soyad, yoksa e-posta) göre sıralanır */
function compareUserListByUserName(
  a: RevisdomAPIViewModelsUserResponse,
  b: RevisdomAPIViewModelsUserResponse,
  direction: "asc" | "desc"
): number {
  const mult = direction === "asc" ? 1 : -1;
  const emailCmp = (a.email || "").localeCompare(b.email || "", undefined, { sensitivity: "base" });
  const nameA = displayNameForUser(a).toLowerCase();
  const nameB = displayNameForUser(b).toLowerCase();
  const nameCmp = nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
  return mult * (nameCmp || emailCmp);
}

function filterUserListRows(
  list: RevisdomAPIViewModelsUserResponse[],
  organizationNeedle: string,
  roleValue: string | undefined
): RevisdomAPIViewModelsUserResponse[] {
  let out = list;
  const org = organizationNeedle.trim().toLowerCase();
  if (org) {
    out = out.filter((u) => (u.organizationName || "").toLowerCase().includes(org));
  }
  if (roleValue) {
    out = out.filter((u) => resolveUserRoleLabel(u.role) === roleValue);
  }
  return out;
}

type UserListTabProps = {
  currentUserId?: number;
  currentUserEmail?: string | null;
  /** Admin + SuperAdmin: ekleme + silme (yonetim) */
  canManageUsers?: boolean;
  currentViewerRole?: string;
};

const UserListTab = ({
  currentUserId,
  currentUserEmail,
  canManageUsers,
  currentViewerRole,
}: UserListTabProps) => {
  const message = useAntdAppMessage();
  const { getAllUsers, deleteUserById } = useSecureService();
  // useSecureService her render'da yeni fonksiyon referansları veriyor; doğrudan
  // dependency'de kullanılırsa useEffect sürekli tetiklenir (spinner loop).
  const getAllUsersRef = useRef(getAllUsers);
  getAllUsersRef.current = getAllUsers;

  const [users, setUsers] = useState<RevisdomAPIViewModelsUserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [userToDelete, setUserToDelete] = useState<RevisdomAPIViewModelsUserResponse | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [filterOrganization, setFilterOrganization] = useState("");
  const [filterRole, setFilterRole] = useState<string | undefined>(undefined);
  const [openUserFilterDropdown, setOpenUserFilterDropdown] = useState(false);
  const [openUserSortDropdown, setOpenUserSortDropdown] = useState(false);
  const [userListSortDirection, setUserListSortDirection] = useState<"asc" | "desc">("asc");
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);

  const userListSortSelectPopupClassName = projectsStyles.projectDetailSortSelectPopup;

  const roleFilterSelectOptions = useMemo(() => {
    const labels = new Set<string>([
      Role.Admin,
      Role.Editor,
      Role.Commenter,
      Role.Viewer,
      Role.SuperAdmin,
      Role.Guest,
    ]);
    for (const u of users) {
      const r = resolveUserRoleLabel(u.role);
      if (r) labels.add(r);
    }
    return Array.from(labels)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      .map((value) => ({ value, label: value }));
  }, [users]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    const res = await getAllUsersRef.current();
    setLoading(false);
    if (res.success) {
      setUsers(res.data);
    } else {
      message.error(res.message || "Failed to load users");
    }
  }, [message]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const activeUsers = useMemo(() => {
    const raw = users.filter((u) => !u.isDeleted);
    const filtered = filterUserListRows(raw, filterOrganization, filterRole);
    return [...filtered].sort((a, b) => compareUserListByUserName(a, b, userListSortDirection));
  }, [users, filterOrganization, filterRole, userListSortDirection]);

  const deletedUsers = useMemo(() => {
    const raw = users.filter((u) => u.isDeleted);
    const filtered = filterUserListRows(raw, filterOrganization, filterRole);
    return [...filtered].sort((a, b) => compareUserListByUserName(a, b, userListSortDirection));
  }, [users, filterOrganization, filterRole, userListSortDirection]);

  const userListFilterActive = !!filterOrganization.trim() || filterRole != null;
  const userListSortActive = userListSortDirection !== "asc";

  const renderRow = (u: RevisdomAPIViewModelsUserResponse) => {
    const displayName =
      [u.firstName, u.lastName].filter(Boolean).join(" ").trim() ||
      u.email ||
      "—";
    const roleLabel = resolveUserRoleLabel(u.role);
    const orgName = String(u.organizationName ?? "").trim();
    const metaParts: { key: string; text: string }[] = [];
    if (u.email) metaParts.push({ key: "email", text: u.email });
    if (roleLabel) metaParts.push({ key: "role", text: roleLabel });
    if (orgName) metaParts.push({ key: "org", text: orgName });

    const showDelete =
      !!canManageUsers && canDeleteListUser(u, currentUserId, currentUserEmail, currentViewerRole);

    return (
      <List.Item
        key={u.id ?? `${u.email}-${u.role}`}
        className={`${layoutStyles.notificationResultItem} ${u.isDeleted ? layoutStyles.notificationResultItemRead : ""}`}
      >
        <div className={layoutStyles.notificationItemContent}>
          <div className={layoutStyles.notificationAvatarWrap}>
            <Avatar
              size={32}
              src={u.thumbnailUrl || undefined}
              icon={<UserOutlined style={{ color: "#F6FFED", fontSize: 14 }} />}
              className={layoutStyles.userAvatar}
            />
          </div>
          <div className={layoutStyles.notificationContent}>
            <div
              className={`${layoutStyles.notificationTitle} ${u.isDeleted ? layoutStyles.notificationTitleRead : ""}`}
            >
              {displayName}
            </div>
            <div
              className={`${layoutStyles.notificationTimeRow} ${u.isDeleted ? layoutStyles.notificationTimeRowRead : layoutStyles.notificationTimeRowUnread}`}
            >
              <div className={styles.userListMetaRow}>
                {metaParts.length === 0 ? (
                  "—"
                ) : (
                  metaParts.map((part, idx) => (
                    <Fragment key={part.key}>
                      {idx > 0 ? (
                        <span className={styles.userListMetaSep} aria-hidden>
                          –
                        </span>
                      ) : null}
                      <span className={styles.userListMetaText}>{part.text}</span>
                    </Fragment>
                  ))
                )}
              </div>
            </div>
          </div>
          {showDelete && (
            <div className={styles.userListRowActions}>
              <Button
                type="default"
                size="small"
                icon={<DeleteOutlined />}
                className={styles.customDeleteButton}
                onClick={() => {
                  setUserToDelete(u);
                  setDeleteModalVisible(true);
                }}
              >
                Delete
              </Button>
            </div>
          )}
        </div>
      </List.Item>
    );
  };

  if (loading) {
    return (
      <div className={styles.userListContent}>
        <Spin size="large" style={{ display: "block", margin: "48px auto" }} />
      </div>
    );
  }

  return (
    <div className={styles.userListContent}>
      <div className={projectsStyles.projectDetailToolbar}>
        <div className={projectsStyles.projectDetailToolbarSegment}>
          <Dropdown
            trigger={["click"]}
            open={openUserFilterDropdown}
            onOpenChange={(open) => {
              setOpenUserFilterDropdown(open);
              if (open) setOpenUserSortDropdown(false);
            }}
            getPopupContainer={() => document.body}
            align={{ offset: [0, 20] }}
            placement="bottomLeft"
            overlayClassName={projectsStyles.projectDetailFilterDropdownOverlay}
            dropdownRender={() => (
              <div
                className={`${projectsStyles.projectDetailFilterDropdown} ${styles.userListFilterDropdown}`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={projectsStyles.projectDetailFilterRow}>
                  <span className={styles.userListFilterIconSlot} aria-hidden>
                    <TeamOutlined className={projectsStyles.projectDetailFilterIcon} />
                  </span>
                  <span className={projectsStyles.projectDetailFilterLabel}>Organization</span>
                  <div className={projectsStyles.projectDetailFilterControlShell}>
                    <Input
                      className={projectsStyles.projectDetailFilterInput}
                      placeholder="Organization name"
                      value={filterOrganization}
                      onChange={(e) => setFilterOrganization(e.target.value)}
                      allowClear
                      aria-label="Filter by organization"
                    />
                  </div>
                </div>
                <div className={projectsStyles.projectDetailFilterRow}>
                  <span className={styles.userListFilterIconSlot} aria-hidden>
                    <img
                      src={`${process.env.PUBLIC_URL}/images/icon/role.svg`}
                      alt=""
                      className={styles.userListFilterRoleIcon}
                      aria-hidden
                    />
                  </span>
                  <span className={projectsStyles.projectDetailFilterLabel}>Role</span>
                  <div className={projectsStyles.projectDetailFilterControlShell}>
                    <Select
                      className={projectsStyles.projectDetailFilterSelect}
                      popupClassName={userListSortSelectPopupClassName}
                      getPopupContainer={() => document.body}
                      placeholder="Select role"
                      allowClear
                      value={filterRole}
                      onChange={(v) => setFilterRole(v != null ? String(v) : undefined)}
                      suffixIcon={<DownOutlined />}
                      options={roleFilterSelectOptions}
                      aria-label="Filter by role"
                    />
                  </div>
                </div>
              </div>
            )}
          >
            <Button
              type="default"
              className={`${projectsStyles.projectDetailToolbarButton} ${
                openUserFilterDropdown || userListFilterActive
                  ? projectsStyles.projectDetailToolbarButtonActive
                  : ""
              }`}
            >
              <span className={projectsStyles.projectDetailToolbarButtonContent}>
                <FilterOutlined />
                <span>Filter User</span>
              </span>
            </Button>
          </Dropdown>
          <Dropdown
            trigger={["click"]}
            open={openUserSortDropdown}
            onOpenChange={(open) => {
              setOpenUserSortDropdown(open);
              if (open) setOpenUserFilterDropdown(false);
            }}
            getPopupContainer={() => document.body}
            align={{ offset: [0, 20] }}
            placement="bottomLeft"
            overlayClassName={projectsStyles.projectDetailSortDropdownOverlay}
            dropdownRender={() => (
              <div
                className={`${projectsStyles.projectDetailSortDropdown} ${styles.userListSortDropdown}`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={projectsStyles.projectDetailFilterRow}>
                  <span className={styles.userListFilterIconSlot} aria-hidden>
                    <UserOutlined className={projectsStyles.projectDetailFilterIcon} />
                  </span>
                  <span className={projectsStyles.projectDetailFilterLabel}>User name</span>
                  <div className={projectsStyles.projectDetailFilterControlShell}>
                    <Select
                      className={projectsStyles.projectDetailSortSelect}
                      popupClassName={userListSortSelectPopupClassName}
                      getPopupContainer={() => document.body}
                      value={userListSortDirection}
                      onChange={(v) => setUserListSortDirection((v ?? "asc") as "asc" | "desc")}
                      suffixIcon={<DownOutlined />}
                      options={[
                        { value: "asc", label: "A to Z" },
                        { value: "desc", label: "Z to A" },
                      ]}
                      aria-label="Sort users by name"
                    />
                  </div>
                </div>
              </div>
            )}
          >
            <Button
              type="default"
              className={`${projectsStyles.projectDetailToolbarButton} ${
                openUserSortDropdown || userListSortActive
                  ? projectsStyles.projectDetailToolbarButtonActive
                  : ""
              }`}
            >
              <span className={projectsStyles.projectDetailToolbarButtonContent}>
                <MenuOutlined />
                <span>Sorted by</span>
              </span>
            </Button>
          </Dropdown>
        </div>
        {canManageUsers && (
          <Button
            type="primary"
            onClick={() => setAddUserModalOpen(true)}
            className={styles.userListAddButton}
          >
            Add new user
          </Button>
        )}
      </div>
      <div className={styles.userListScroll}>
        <List
          itemLayout="horizontal"
          dataSource={activeUsers}
          locale={{ emptyText: "No active users" }}
          renderItem={renderRow}
        />
        {deletedUsers.length > 0 && (
          <>
            <div
              className={`${styles.userListSectionTitle} ${styles.userListSectionTitleBleedLine}`}
            >
              Deleted Users
            </div>
            <List
              itemLayout="horizontal"
              dataSource={deletedUsers}
              renderItem={renderRow}
            />
          </>
        )}
      </div>
      <Modal
        centered
        title={
          <div className={deleteModalStyles.modalHeader}>
            <span className={deleteModalStyles.modalTitle}>New User</span>
            <CloseOutlined
              onClick={() => setAddUserModalOpen(false)}
              className={deleteModalStyles.closeIcon}
            />
          </div>
        }
        open={addUserModalOpen}
        onCancel={() => setAddUserModalOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
        maskClosable={false}
        className={deleteModalStyles.deleteModal}
        getContainer={() => document.body}
      >
        <div className={deleteModalStyles.modalContent}>
          <div className={styles.userListAddUserModalBody}>
            <ConfigProvider
              getPopupContainer={(node) =>
                (node?.closest(".ant-modal-content") as HTMLElement) || document.body
              }
            >
              <div className={styles.userListAddUserModalForm}>
                <AddUserForm
                  darkAppearance
                  onCancel={() => setAddUserModalOpen(false)}
                  onUserCreated={async () => {
                    setAddUserModalOpen(false);
                    await loadUsers();
                  }}
                />
              </div>
            </ConfigProvider>
          </div>
        </div>
      </Modal>
      <DeleteModal
        visible={deleteModalVisible}
        titleOverride={
          userToDelete?.email
            ? `Delete user: ${userToDelete.email}`
            : "Delete user"
        }
        loading={deleteLoading}
        onCancel={() => {
          setDeleteModalVisible(false);
          setUserToDelete(null);
        }}
        onConfirm={async () => {
          if (userToDelete?.id == null) return;
          setDeleteLoading(true);
          const r = await deleteUserById(userToDelete.id);
          setDeleteLoading(false);
          if (r.success) {
            message.success("User deleted.");
            setDeleteModalVisible(false);
            setUserToDelete(null);
            await loadUsers();
          } else {
            message.error((r as { message?: string }).message || "Delete failed.");
          }
        }}
      />
    </div>
  );
};

const Settings = () => {
  const message = useAntdAppMessage();
  const user = useAtomValue(userAtom);
  const { editUser } = useSecureService();
  const { canUpdateOwnProfile } = useAuth();

  const canManageUsers = user && canManageUserAccounts(user.role);
  const canViewUserList = canManageUsers;

  // Form state'leri
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [emailNotification, setEmailNotification] = useState(true);
  const [loadingUserData, setLoadingUserData] = useState(true);
  const avatarFileInputRef = useRef<HTMLInputElement>(null);

  const watchedFirstName = Form.useWatch("firstName", profileForm);
  const watchedLastName = Form.useWatch("lastName", profileForm);
  const watchedTitle = Form.useWatch("title", profileForm);
  const watchedOldPassword = Form.useWatch("oldPassword", profileForm);
  const watchedNewPassword = Form.useWatch("newPassword", profileForm);

  const hasUnsavedProfileChanges = useMemo(() => {
    if (!user || loadingUserData) return false;
    const baselineNotify = (user as { notificationToEmail?: boolean }).notificationToEmail ?? true;
    if (emailNotification !== baselineNotify) return true;
    if (avatarFile) return true;
    const fn = String(watchedFirstName ?? "").trim();
    const ln = String(watchedLastName ?? "").trim();
    const tt = String(watchedTitle ?? "").trim();
    if (fn !== String(user.firstName ?? "").trim()) return true;
    if (ln !== String(user.lastName ?? "").trim()) return true;
    if (tt !== String(user.title ?? "").trim()) return true;
    const op = String(watchedOldPassword ?? "").trim();
    const np = String(watchedNewPassword ?? "").trim();
    if (op && np) return true;
    return false;
  }, [
    user,
    loadingUserData,
    emailNotification,
    avatarFile,
    watchedFirstName,
    watchedLastName,
    watchedTitle,
    watchedOldPassword,
    watchedNewPassword,
  ]);

  const profileHeroName = useMemo(() => {
    const fn = String(watchedFirstName ?? user?.firstName ?? "").trim();
    const ln = String(watchedLastName ?? user?.lastName ?? "").trim();
    const combined = `${fn} ${ln}`.trim();
    return combined || String(user?.email ?? "").trim() || "—";
  }, [watchedFirstName, watchedLastName, user?.firstName, user?.lastName, user?.email]);

  const profileHeroTitle = useMemo(() => {
    const t = String(watchedTitle ?? user?.title ?? "").trim();
    return t || "—";
  }, [watchedTitle, user?.title]);

  const avatarDisplaySrc = useMemo(() => {
    if (avatarFile) return URL.createObjectURL(avatarFile);
    return userData?.thumbnailUrl ?? undefined;
  }, [avatarFile, userData?.thumbnailUrl]);

  useEffect(() => {
    return () => {
      if (avatarFile && typeof avatarDisplaySrc === "string" && avatarDisplaySrc.startsWith("blob:")) {
        URL.revokeObjectURL(avatarDisplaySrc);
      }
    };
  }, [avatarFile, avatarDisplaySrc]);

  // Kullanıcı bilgilerini yükle
  useEffect(() => {
    if (user) {
      setUserData(user);
      profileForm.setFieldsValue({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        title: user.title || "",
        oldPassword: "",
        newPassword: "",
      });

      // Email notification ayarını API'den oku
      setEmailNotification((user as any).notificationToEmail ?? true);

      setLoadingUserData(false);
    } else {
      setLoadingUserData(true);
    }
  }, [user, profileForm]);

  // Profil güncelleme fonksiyonu
  const handleProfileUpdate = async (values: any) => {
    if (!canUpdateOwnProfile) {
      message.error("You do not have permission to update your profile.");
      return;
    }
    setLoading(true);
    try {
      const updateData: any = {};

      if (values.firstName) updateData.firstName = values.firstName;
      if (values.lastName) updateData.lastName = values.lastName;
      if (values.title) updateData.title = values.title;
      if (avatarFile) updateData.thumbnail = avatarFile;

      if (values.oldPassword && values.newPassword) {
        updateData.oldPassword = values.oldPassword;
        updateData.newPassword = values.newPassword;
      }

      // Email notification ayarını API'ye gönder (backend hazır olduğunda çalışacak)
      updateData.notificationToEmail = emailNotification;

      const response = await editUser(updateData);

      if (response.success) {
        message.success("Profile updated successfully.");
        profileForm.setFieldsValue({ oldPassword: "", newPassword: "" });
        // User bilgileri otomatik olarak güncellenecek
      } else {
        message.error("An error occurred while updating profile.");
      }
    } catch (error) {
      message.error("An error occurred while updating profile.");
    } finally {
      setLoading(false);
    }
  };

  const onAvatarFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      message.warning("Please select an image file.");
      return;
    }
    setAvatarFile(file);
  };

  const openAvatarPicker = () => {
    avatarFileInputRef.current?.click();
  };

  // Email notification değişikliği
  const handleEmailNotificationChange = (checked: boolean) => {
    setEmailNotification(checked);
    // Değişiklik hemen kayıt edilsin mi yoksa Save Changes butonuyla mı kaydedilsin?
    // Şimdilik state'i güncelleyip, Save Changes ile kaydedilmesini sağlayalım
  };

  const handleProfileFormReset = () => {
    if (!user) return;
    profileForm.setFieldsValue({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      title: user.title || "",
      oldPassword: "",
      newPassword: "",
    });
    setAvatarFile(null);
    setUserData(user);
    setEmailNotification((user as { notificationToEmail?: boolean }).notificationToEmail ?? true);
  };

  const openPasswordModal = () => {
    passwordForm.resetFields();
    setPasswordModalOpen(true);
  };

  const closePasswordModal = () => {
    setPasswordModalOpen(false);
    passwordForm.resetFields();
  };

  const handlePasswordModalSubmit = async () => {
    let values: { oldPassword: string; newPassword: string; confirmPassword: string };
    try {
      values = await passwordForm.validateFields();
    } catch {
      return;
    }
    profileForm.setFieldsValue({
      oldPassword: values.oldPassword,
      newPassword: values.newPassword,
    });
    closePasswordModal();
    message.info("Click Save Changes to apply your new password.");
  };

  const userSettingsTab = {
    key: "user",
    label: "User Settings",
    children: (
      <div className={styles.userSettingsContent}>
        <Row gutter={24}>
          {/* Avatar bölümü - Üst satır */}
          <Col span={24} className={styles.avatarSection}>
            <div className={styles.profileHero}>
              {loadingUserData ? (
                <div className={styles.loadingWrapper}>
                  <div className={styles.loadingText}>Loading...</div>
                </div>
              ) : (
                <>
                  <div className={styles.profileHeroMain}>
                    <input
                      ref={avatarFileInputRef}
                      type="file"
                      accept="image/*"
                      className={styles.hiddenFileInput}
                      onChange={onAvatarFileInputChange}
                      tabIndex={-1}
                    />
                    <button
                      type="button"
                      className={styles.profileAvatarTrigger}
                      onClick={openAvatarPicker}
                      aria-label="Change profile photo"
                      disabled={!canUpdateOwnProfile}
                    >
                      <Avatar
                        className={styles.profileAvatarInner}
                        size={72}
                        src={avatarDisplaySrc}
                        icon={<UserOutlined />}
                      />
                      <span className={styles.profileAvatarHoverLayer} aria-hidden>
                        <EditOutlined />
                      </span>
                    </button>
                    <div className={styles.profileHeroText}>
                      <div className={styles.profileHeroName}>{profileHeroName}</div>
                      <div className={styles.profileHeroTitle}>{profileHeroTitle}</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </Col>

          {/* Form alanları */}
          <Col span={24}>
            <Form
              form={profileForm}
              layout="vertical"
              onFinish={handleProfileUpdate}
              className={styles.profileForm}
            >
              <div className={styles.profileFormGrid}>
                <Form.Item
                  className={styles.profileFormItem}
                  label="First Name"
                  name="firstName"
                  rules={[{ required: true, message: "First name is required!" }]}
                >
                  <Input placeholder="Enter first name" disabled={!canUpdateOwnProfile} />
                </Form.Item>
                <Form.Item
                  className={styles.profileFormItem}
                  label="Last Name"
                  name="lastName"
                  rules={[{ required: true, message: "Last name is required!" }]}
                >
                  <Input placeholder="Enter last name" disabled={!canUpdateOwnProfile} />
                </Form.Item>
                <Form.Item className={styles.profileFormItem} label="Job Title" name="title">
                  <Input placeholder="Enter job title" disabled={!canUpdateOwnProfile} />
                </Form.Item>
                <Form.Item className={styles.profileFormItem} label="E-mail">
                  <Input readOnly value={user?.email ?? ""} placeholder="—" />
                </Form.Item>
                <Form.Item className={styles.profileFormItem} label="Password">
                  <Input
                    readOnly
                    autoComplete="off"
                    placeholder="••••••••"
                    suffix={
                      <button
                        type="button"
                        className={styles.passwordFieldSuffix}
                        aria-label="Change password"
                        onClick={(e) => {
                          e.preventDefault();
                          if (canUpdateOwnProfile) openPasswordModal();
                        }}
                        disabled={!canUpdateOwnProfile}
                      >
                        <EditOutlined />
                      </button>
                    }
                  />
                </Form.Item>
              </div>
              <Form.Item name="oldPassword" hidden>
                <Input type="password" autoComplete="off" />
              </Form.Item>
              <Form.Item name="newPassword" hidden>
                <Input type="password" autoComplete="off" />
              </Form.Item>
              <div className={styles.notificationSection}>
                <span className={styles.notificationLabel}>
                  Notification to email:
                </span>
                <Switch
                  checked={emailNotification}
                  onChange={handleEmailNotificationChange}
                  disabled={!canUpdateOwnProfile}
                  style={{
                    backgroundColor: emailNotification
                      ? "var(--app-accent-color)"
                      : "#666",
                  }}
                />
              </div>

              <div className={styles.profileFormActions}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  disabled={!hasUnsavedProfileChanges || !canUpdateOwnProfile}
                  className={styles.profileFormSaveButton}
                >
                  Save Changes
                </Button>
                <Button htmlType="button" type="default" className={styles.profileFormCancelButton} onClick={handleProfileFormReset}>
                  Cancel
                </Button>
              </div>
            </Form>
          </Col>
        </Row>
      </div>
    ),
  };

  const tabItems = [
    userSettingsTab,
    ...(canViewUserList
      ? [
          {
            key: "user-list",
            label: "User List",
            children: (
              <UserListTab
                currentUserId={user?.id}
                currentUserEmail={user?.email}
                canManageUsers={!!canManageUsers}
                currentViewerRole={user?.role}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <MainLayout title="Settings" icon={<SettingOutlined />}>
      <div className={styles.settingsContainer}>
        <div className={styles.tabsWrapper}>
          <Tabs defaultActiveKey="user" items={tabItems} />
        </div>
        <Modal
          title={null}
          closable={false}
          open={passwordModalOpen}
          onCancel={closePasswordModal}
          footer={null}
          width={400}
          centered
          destroyOnClose
          className={styles.changePasswordModal}
          maskClosable
        >
          <div className={styles.changePasswordModalContent}>
            <Form
              form={passwordForm}
              layout="horizontal"
              requiredMark={false}
              colon={false}
              labelAlign="left"
              labelCol={{ flex: "120px" }}
              wrapperCol={{ flex: 1 }}
              className={styles.changePasswordForm}
              preserve={false}
            >
              <Form.Item
                className={styles.profileFormItem}
                label="Old Password"
                name="oldPassword"
                rules={[{ required: true, message: "Enter your current password." }]}
              >
                <Input.Password placeholder="Enter Old Password" />
              </Form.Item>
              <Form.Item
                className={styles.profileFormItem}
                label="New Password"
                name="newPassword"
                rules={[
                  { required: true, message: "Enter a new password." },
                  { min: 6, message: "Password must be at least 6 characters." },
                ]}
              >
                <Input.Password placeholder="Enter New Password" />
              </Form.Item>
              <Form.Item
                className={styles.profileFormItem}
                label="Confirm Password"
                name="confirmPassword"
                dependencies={["newPassword"]}
                rules={[
                  { required: true, message: "Confirm your new password." },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("newPassword") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error("Passwords do not match."));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Re-enter New Password" />
              </Form.Item>
            </Form>
            <div className={styles.changePasswordActionButtons}>
              <CustomButton
                onClick={closePasswordModal}
                className={styles.changePasswordCancelButton}
              >
                Cancel
              </CustomButton>
              <CustomButton onClick={handlePasswordModalSubmit} className={styles.changePasswordSubmitButton}>
                Change Password
              </CustomButton>
            </div>
          </div>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default Settings;
